import { AIAnalysis } from './ai.js';
import { Markdown } from './markdown.js';

export default class AIUI {
	static currentSessionId: number | null = null;
	
	static init() {
		// Tab Navigation
		const summaryBtn = document.getElementById('tab-btn-summary');
		if (summaryBtn) { summaryBtn.addEventListener('click', () => AIUI.showTab('summary')); }
		
		const chatBtn = document.getElementById('tab-btn-chat');
		if (chatBtn) { chatBtn.addEventListener('click', () => AIUI.showTab('chat')); }
		
		const welcomeSummaryCard = document.getElementById('welcome-summary-card');
		if (welcomeSummaryCard) { welcomeSummaryCard.addEventListener('click', () => AIUI.showTab('summary')); }
		
		const welcomeChatCard = document.getElementById('welcome-chat-card');
		if (welcomeChatCard) { welcomeChatCard.addEventListener('click', () => AIUI.showTab('chat')); }
		
		// Listen for tab changes
		window.addEventListener('tab-changed', (e: any) => {
			if (e.detail.tab === 'summary') {
				// Only load if empty or if we want to refresh on tab switch (optional)
				const contentDiv = document.getElementById('summary-content');
				if (contentDiv && (contentDiv.innerHTML.trim() === 'Loading analysis...' || contentDiv.innerHTML.trim() === '')) { AIUI.loadSummary(false); }
			}
		});
		
		// Summary Tab
		const regenerateBtn = document.getElementById('regenerate-summary-btn');
		if (regenerateBtn) { regenerateBtn.addEventListener('click', () => AIUI.loadSummary(true)); }
		
		// Chat Tab
		const sendBtn = document.getElementById('send-chat-btn');
		const inputField = document.getElementById('chat-input-field') as HTMLInputElement;
		const newChatBtn = document.getElementById('new-chat-btn');
		
		if (sendBtn && inputField) {
			sendBtn.addEventListener('click', () => AIUI.sendMessage());
			inputField.addEventListener('keypress', (e) => {
				if (e.key === 'Enter') { AIUI.sendMessage(); }
			});
		}
		
		if (newChatBtn) { newChatBtn.addEventListener('click', () => AIUI.startNewChat()); }
		if (document.getElementById('chat-sessions-list')) { AIUI.loadChatSessions(); }
		
		const includeSummaryCheckbox = document.getElementById('include-summary-checkbox');
		
		if (includeSummaryCheckbox) {
			includeSummaryCheckbox.addEventListener('change', (e) => {
				const checked = (e.target as HTMLInputElement).checked;
				AIUI.toggleContextPanel(checked);
			});
		}
	}
	
	static showTab(tabName: string) {
		document.querySelectorAll('.tab-content').forEach((el: any) => { el.style.display = 'none'; });
		document.querySelectorAll('.tabs button').forEach(el => { el.classList.remove('active'); });
		
		const tab = document.getElementById('tab-' + tabName);
		if (tab) { tab.style.display = 'block'; }
		
		const btn = document.getElementById('tab-btn-' + tabName);
		if (btn) { btn.classList.add('active'); }
		
		window.dispatchEvent(new CustomEvent('tab-changed', { detail: { tab: tabName } }));
	}
	
	static async toggleContextPanel(show: boolean) {
		const panel = document.getElementById('chat-context-panel');
		const content = document.getElementById('chat-context-content');
		if (!panel || !content) { return; }
		
		if (show) {
			panel.style.display = 'block';
			
			if (content.innerHTML.trim() === '') {
				content.innerHTML = 'Loading summary...';
				
				try {
					const result = await AIAnalysis.getAccountSummary(false);
					const data = result.payload || result;
					
					if (data.summaryMarkdown) { content.innerHTML = AIUI.renderMarkdown(data.summaryMarkdown); }
					else { content.innerHTML = 'No summary available.'; }
				} catch (e) {
					content.innerHTML = 'Error loading summary.';
				}
			}
		} else {
			panel.style.display = 'none';
		}
	}
	
	static async loadSummary(force: boolean) {
		const contentDiv = document.getElementById('summary-content');
		const dateSpan = document.getElementById('summary-date');
		const countSpan = document.getElementById('summary-count');
		
		if (contentDiv) { AIUI.showLoading(contentDiv); }
		
		try {
			const result = await AIAnalysis.getAccountSummary(force);
			
			if (result.ok === false && result.payload?.reason?.includes('403')) {
				if (contentDiv) { contentDiv.innerHTML = AIUI.getUpgradeMessage(); }
				return;
			}
			
			// Handle successful response structure from network.ts
			// network.ts returns { ok: true, payload: { summaryMarkdown: "...", ... } }
			const data = result.payload || result;
			
			if (data.summaryMarkdown) {
				if (contentDiv) { contentDiv.innerHTML = AIUI.renderMarkdown(data.summaryMarkdown); }
				
				if (dateSpan) {
					const user = (window as any).user;
					const date = new Date(data.generatedAt);
					
					if (user && user.dateFormat) {
						const day = date.getDate().toString().padStart(2, '0');
						const month = (date.getMonth() + 1).toString().padStart(2, '0');
						const year = date.getFullYear();
						
						let dateStr = user.dateFormat
							.replace('DD', day)
							.replace('MM', month)
							.replace('YYYY', year);
							
						// Append time
						dateStr += ', ' + date.toLocaleTimeString();
						
						dateSpan.innerText = dateStr;
					} else {
						dateSpan.innerText = date.toLocaleString();
					}
				}
				
				if (countSpan) { countSpan.innerText = data.tradeCount; }
			} else {
				if (contentDiv) { contentDiv.innerHTML = 'No analysis available.'; }
			}
		} catch (e) {
			if (contentDiv) { contentDiv.innerHTML = 'Error loading analysis.'; }
		}
	}
	
	static showLoading(container: HTMLElement) {
		const messages = [
			"Analysing Trade Data",
			"Reviewing Stop Losses",
			"Calculating Win Rates",
			"Identifying Patterns",
			"Generating Insights",
			"Checking Market Sentiment",
			"Scanning Economic Calendar",
			"Evaluating Risk-Reward Ratio",
			"Verifying Position Sizing",
			"Assessing Overall Trend",
			"Confirming Support & Resistance",
			"Reviewing Volume Profile",
			"Analyzing Recent News Impact",
			"Checking Open Positions",
			"Validating Entry Criteria",
			"Measuring Volatility Levels",
			"Reviewing Overnight Price Action",
			"Confirming Indicator Alignment",
			"Scanning for Divergences",
			"Evaluating Liquidity Conditions",
			"Checking Correlation with Majors",
			"Assessing Fundamental Catalysts",
			"Reviewing Trade Journal Notes",
			"Calculating Max Drawdown Risk",
			"Confirming Multi-Timeframe Agreement",
			"Analyzing Order Flow",
			"Checking for Upcoming Events",
			"Verifying Exit Strategy",
			"Reviewing Emotional State",
			"Finalizing Trade Probability",
			"Scanning for Hidden Risks",
			"Cross-checking Chart Patterns",
			"Evaluating Market Regime",
			"Confirming Price Action Setup",
			"Reviewing Recent Performance Metrics"
		];
		
		let msgIndex = 0;
		
		container.innerHTML = `
			<div style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px;">
				<div class="spinner" style="border: 4px solid rgba(0, 0, 0, 0.1); width: 36px; height: 36px; border-radius: 50%; border-left-color: var(--theme-accent-color); animation: spin 1s linear infinite;"></div>
				<div id="loading-msg" style="margin-top: 15px; font-style: italic; color: var(--theme-text-secondary);">Analysing Trade Data...</div>
			</div>
			<style>
				@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
			</style>
		`;

		const msgDiv = container.querySelector('#loading-msg');
		
		if (msgDiv) {
			const interval = setInterval(() => {
				if (!document.body.contains(msgDiv)) {
					clearInterval(interval);
					return;
				}
				
				msgIndex = (msgIndex + 1) % messages.length;
				msgDiv.innerHTML = messages[msgIndex] + "...";
			}, 2000);
		}
	}
	
	static async loadChatSessions() {
		const list = document.getElementById('chat-sessions-list');
		if (!list) { return; }
		
		const result = await AIAnalysis.getChatSessions();
		if (!result.ok || !Array.isArray(result.payload)) { return; }
		
		const sessions = result.payload;
		list.innerHTML = '';
		
		sessions.forEach((session: any) => {
			const div = document.createElement('div');
			div.className = 'session-item';
			div.style.padding = '10px';
			div.style.cursor = 'pointer';
			div.style.borderBottom = '1px solid var(--theme-border-color)';
			div.style.display = 'flex';
			div.style.justifyContent = 'space-between';
			div.style.alignItems = 'center';
			
			const titleSpan = document.createElement('span');
			titleSpan.innerText = session.title || `Session ${session.id}`;
			titleSpan.style.flexGrow = '1';
			titleSpan.onclick = () => { AIUI.loadSession(session.id); };
			
			const deleteBtn = document.createElement('button');
			deleteBtn.innerHTML = '&times;';
			deleteBtn.style.background = 'none';
			deleteBtn.style.border = 'none';
			deleteBtn.style.color = 'var(--theme-text-secondary)';
			deleteBtn.style.fontSize = '18px';
			deleteBtn.style.cursor = 'pointer';
			deleteBtn.style.padding = '0 5px';
			deleteBtn.title = 'Delete Session';
			
			deleteBtn.onclick = async (e) => {
				e.stopPropagation();
				
				if (confirm('Are you sure you want to delete this chat session?')) {
					const delResult = await AIAnalysis.deleteChatSession(session.id);
					
					if (delResult.ok) {
						AIUI.loadChatSessions();
						if (AIUI.currentSessionId === session.id) { AIUI.startNewChat(); }
					}
				}
			};
			
			div.appendChild(titleSpan);
			div.appendChild(deleteBtn);
			list.appendChild(div);
		});
	}
	
	static async loadSession(sessionId: number) {
		AIUI.currentSessionId = sessionId;
		const messagesDiv = document.getElementById('chat-messages');
		if (messagesDiv) { messagesDiv.innerHTML = 'Loading...'; }
		
		const result = await AIAnalysis.getChatMessages(sessionId);
		
		if (result.ok && Array.isArray(result.payload)) {
			if (messagesDiv) { messagesDiv.innerHTML = ''; }
			result.payload.forEach((msg: any) => { AIUI.appendMessage(msg.role, msg.content); });
		} else {
			if (messagesDiv) { messagesDiv.innerHTML = 'Failed to load messages.'; }
		}
	}
	
	static startNewChat() {
		AIUI.currentSessionId = null;
		const messagesDiv = document.getElementById('chat-messages');
		if (messagesDiv) { messagesDiv.innerHTML = ''; }
	}
	
	static async sendMessage() {
		const inputField = document.getElementById('chat-input-field') as HTMLInputElement;
		const includeSummaryCheckbox = document.getElementById('include-summary-checkbox') as HTMLInputElement;
		const message = inputField.value;
		if (!message) { return; }
		
		const includeSummary = includeSummaryCheckbox ? includeSummaryCheckbox.checked : false;
		
		inputField.value = '';
		AIUI.appendMessage('user', message);
		
		let assistantMessageDiv = AIUI.appendMessage('assistant', '');
		
		// Show waiting animation
		let waitingInterval: any = null;
		let waitingDots = 0;
		assistantMessageDiv.innerHTML = '<span style="font-style:italic; color:var(--theme-text-secondary);">Waiting for AI</span>';
		
		waitingInterval = setInterval(() => {
			waitingDots = (waitingDots + 1) % 4;
			assistantMessageDiv.innerHTML = '<span style="font-style:italic; color:var(--theme-text-secondary);">Waiting for AI' + '.'.repeat(waitingDots) + '</span>';
		}, 500);
		
		let fullResponse = '';
		let firstChunk = true;
		
		await AIAnalysis.sendChatMessage(AIUI.currentSessionId, message, null, includeSummary, (text) => {
			if (firstChunk) {
				clearInterval(waitingInterval);
				firstChunk = false;
			}
			
			if (text === '403_FORBIDDEN') {
				assistantMessageDiv.innerHTML = AIUI.getUpgradeMessage();
				AIUI.scrollToBottom(true);
			} else {
				// Check if we are near bottom BEFORE adding content
				const messagesDiv = document.getElementById('chat-messages');
				const wasAtBottom = messagesDiv ? (messagesDiv.scrollHeight - messagesDiv.scrollTop - messagesDiv.clientHeight < 150) : true;
				
				fullResponse += text;
				assistantMessageDiv.innerHTML = Markdown.parse(fullResponse);
				
				if (wasAtBottom) { AIUI.scrollToBottom(true); }
			}
		}, (newSessionId) => {
			if (AIUI.currentSessionId !== newSessionId) {
				AIUI.currentSessionId = newSessionId;
				AIUI.loadChatSessions(); // Refresh sidebar to show new session
			}
		});
		
		// Ensure interval is cleared if it finishes instantly or errors
		clearInterval(waitingInterval);
		
		// Refresh sessions list if it was a new chat
		if (!AIUI.currentSessionId) { AIUI.loadChatSessions(); }
	}
	
	static getUpgradeMessage() {
		return `
		<div style="text-align: center; padding: 20px; background: var(--bg-secondary); border-radius: 8px; border: 1px solid var(--theme-border-color);">
			<h3>Upgrade Required</h3>
			<p>Unfortunately your subscription level doesn't give you access to this feature.</p>
			<a href="/pricing" class="btn-primary" style="display: inline-block; margin-top: 10px; text-decoration: none;">Click here to upgrade</a>
		</div>
		`;
	}
	
	static appendMessage(role: 'user' | 'assistant', text: string) {
		const messagesDiv = document.getElementById('chat-messages');
		const div = document.createElement('div');
		div.className = `chat-message ${role}`;
		
		if (role === 'assistant') {
			div.classList.add('markdown-body');
			div.innerHTML = Markdown.parse(text);
		} else {
			div.innerText = text;
		}
		
		if (messagesDiv) {
			messagesDiv.appendChild(div);
			AIUI.scrollToBottom(true);
		}
		
		return div;
	}
	
	static scrollToBottom(force: boolean = false) {
		const messagesDiv = document.getElementById('chat-messages');
		if (!messagesDiv) { return; }
		
		requestAnimationFrame(() => {
			if (force) {
				messagesDiv.scrollTop = messagesDiv.scrollHeight;
			} else {
				// Smart scroll: only scroll if user is near the bottom
				const threshold = 150;
				const distanceToBottom = messagesDiv.scrollHeight - messagesDiv.scrollTop - messagesDiv.clientHeight;
				if (distanceToBottom < threshold) { messagesDiv.scrollTop = messagesDiv.scrollHeight; }
			}
		});
	}
	
	static renderMarkdown(text: string) {
		return Markdown.parse(text);
	}
}
